import { ChevronDown, ChevronUp } from 'components/icons'
import React, { useEffect, useRef, useState } from 'react'
import styles from './Accordion.module.sass'

interface Props {
  id: string
  label: string
  variant?: 'filter' | 'more' | 'box'
  contentPosition?: 'top' | 'bottom'
  isOpen?: boolean
  children?: React.ReactNode
  className?: string
  labelClassName?: string
  onClick?: () => void
}

const Accordion = (props: Props) => {
  const {
    id = undefined,
    label = undefined,
    variant = 'more',
    children = undefined,
    contentPosition = 'bottom',
    isOpen = false,
    className = '',
    onClick = () => undefined,
    labelClassName = '',
  } = props

  const [open, setOpen] = useState(false)
  const [height, setHeight] = useState<number | undefined>(undefined)
  const innerContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) setOpen(true)
  }, [])

  useEffect(() => {
    if (!innerContentRef.current) return

    const updateHeight = () => {
      const newHeight = open ? innerContentRef.current?.scrollHeight : 0
      setHeight(newHeight)
    }

    updateHeight()
    // Update height on window resize
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [open, innerContentRef])

  return (
    <div
      className={`${styles.root} ${open ? styles['root--open'] : ''} ${
        variant ? styles[`root--${variant}`] : ''
      } ${className}`}
    >
      {contentPosition === 'top' && (
        <div
          id={`${id}_panel`}
          aria-labelledby={`${id}_label`}
          className={styles.content}
          style={{
            height: height !== undefined ? `${height}px` : undefined,
          }}
        >
          <div className={styles.innerContent} ref={innerContentRef}>
            {children}
          </div>
        </div>
      )}
      <p id={`${id}_label`}>
        <button
          type="button"
          className={styles.trigger}
          onClick={() => {
            setOpen(!open)
            onClick()
          }}
          aria-expanded={open}
          aria-controls={`${id}_panel`}
        >
          <span className={labelClassName}>{label}</span>
          <span className={styles.arrow}>{open ? <ChevronUp /> : <ChevronDown />}</span>
        </button>
      </p>
      {contentPosition === 'bottom' && (
        <div
          id={`${id}_panel`}
          aria-labelledby={`${id}_label`}
          className={styles.content}
          style={{
            height: height !== undefined ? `${height}px` : undefined,
          }}
        >
          <div className={styles.innerContent} ref={innerContentRef}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
export default Accordion
